import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { IJob } from '@biolibtech/biolib-js';
import { IMultipartMetadata } from '@biolibtech/biolib-js/src/BioLib/resources/Job.types';
import { rewriteUrlToOrigin } from './baseApi';
import { sleep } from '@utils';

const MULTIPART_UPLOAD_CHUNK_SIZE = 50_000_000; // 50 MB
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 3_000;

type JobsBaseQueryFn = BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>;
type JobsBaseQuery = (arg: Parameters<JobsBaseQueryFn>[0]) => ReturnType<JobsBaseQueryFn>;

function isServerOrNetworkErrorStatus(status: number | string | undefined): boolean {
    if (typeof status === 'number') {
        return status >= 500;
    }
    return status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR';
}

export async function queryWithRetries(
    baseQuery: JobsBaseQuery,
    arg: Parameters<JobsBaseQuery>[0],
): Promise<Awaited<ReturnType<JobsBaseQuery>>> {
    let response = await baseQuery(arg);
    for (let attempt = 1; attempt < MAX_RETRIES && response.error && isServerOrNetworkErrorStatus(response.error.status); attempt += 1) {
        await sleep(RETRY_DELAY_MS);
        response = await baseQuery(arg);
    }
    return response;
}

async function uploadPart(
    url: string,
    chunk: Uint8Array,
    partNumber: number,
): Promise<{ etag: string } | { error: FetchBaseQueryError }> {
    let errorMessage: string | null = null;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt += 1) {
        if (attempt > 0) {
            await sleep(RETRY_DELAY_MS);
        }
        try {
            const response = await fetch(url, {
                method: 'PUT',
                body: chunk,
                headers: { 'BioLib-Client': 'biolib-frontend', 'Content-Type': '' },
            });
            const etag = response.headers.get('etag');
            if (response.ok && etag) {
                return { etag };
            }
            errorMessage = `Got status ${response.status}${etag ? '' : ' and no ETag'}`;
            // Only server errors are transient; fail fast on 4xx or a successful response missing an ETag.
            if (response.status < 500) {
                break;
            }
        } catch (error) {
            errorMessage = `${error}`;
        }
    }
    console.error(`Failed to upload input part ${partNumber}: ${errorMessage}`);
    return { error: { status: 'CUSTOM_ERROR', error: `Failed to upload input part ${partNumber}: ${errorMessage}` } };
}

export async function uploadModuleInput(
    job: IJob,
    inputSerialized: Uint8Array,
    baseQuery: JobsBaseQuery,
): Promise<FetchBaseQueryError | undefined> {
    const headers = { 'Job-Auth-Token': job.auth_token };

    const startUploadResponse = await queryWithRetries(baseQuery, {
        url: `/jobs/${job.public_id}/storage/input/start_upload/`,
        method: 'POST',
        headers,
        body: {},
    });
    if (startUploadResponse.error) {
        return startUploadResponse.error;
    }

    const partCount = Math.ceil(inputSerialized.byteLength / MULTIPART_UPLOAD_CHUNK_SIZE);
    const parts: IMultipartMetadata[] = [];
    for (let i = 0; i < partCount; i += 1) {
        const part_number = i + 1;

        const presignedUrlResponse = await queryWithRetries(baseQuery, {
            url: `/jobs/${job.public_id}/storage/input/presigned_upload_url/`,
            headers,
            params: { part_number },
        });
        if (presignedUrlResponse.error) {
            return presignedUrlResponse.error;
        }
        const { presigned_upload_url } = presignedUrlResponse.data as { presigned_upload_url: string };

        const start = i * MULTIPART_UPLOAD_CHUNK_SIZE;
        const chunk = inputSerialized.slice(start, start + MULTIPART_UPLOAD_CHUNK_SIZE);
        const uploadResult = await uploadPart(rewriteUrlToOrigin(presigned_upload_url), chunk, part_number);
        if ('error' in uploadResult) {
            return uploadResult.error;
        }
        parts.push({ ETag: uploadResult.etag, PartNumber: part_number });
    }

    const completeUploadResponse = await queryWithRetries(baseQuery, {
        url: `/jobs/${job.public_id}/storage/input/complete_upload/`,
        method: 'POST',
        headers,
        body: { parts, size_bytes: inputSerialized.byteLength },
    });
    return completeUploadResponse.error;
}
